import { FC, useEffect, useState } from "react";
import { Button, color } from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import Line from "../../assets/LineHorizontal.png";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";

import { useDispatch } from "react-redux";
import {
  setRepTitle,
  setRepFirstName,
  setRepLastName,
  setUserFirstName,
  setUserLastName,
  setRepEmail as setRepEmailRedux,
  setMerchantTitle as setMerchantTitleRedux,
  ShareholderDirector,
  setShareholderDirector,
} from "../../features/merchant/rep";
import { useStyles } from "./BorrowerRep.styles";
import { ToastContainer, toast } from "react-toastify";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import {
  CompanyRole,
  ShareholderDirectorField,
} from "@funded-here-interface/common/src/constant/enum";
import ShareholdersDirectors from "../../components/ShareholdersDirectors/ShareholdersDirectors";
import User from "./Components/User/User";
import Auth from "../../components/Auth/Auth";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { EMAIL_REGEX_VALIDATION } from "@funded-here-interface/common/src/regex/InputRegex";

const BorrowerRep: FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const userEmail = useSelector((state: RootState) => state.auth.userEmail);

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setRepresentativeFirstName(data.repFirstName);
            setRepresentativeLastName(data.repLastName);
            setMerchantFirstName(data.userFirstName);
            setMerchantLastName(data.userLastName);
            setRepEmail(data.repEmail);
            setRepresentativeTitle(data.repTitle);
            setMerchantTitle(data.merchantTitle);
            setAuthChecked(data.authChecked || false);
            setDirectorChecked(data.directorChecked || false);

            if (data.shareHoldersDirectors?.length > 0) {
              setShareholdersDirectors(data.shareHoldersDirectors);
              setNumOfShareholdersDirectors(data.shareHoldersDirectors.length);
            }

            dispatch(setRepFirstName({ repFirstName: data.repFirstName }));
            dispatch(setRepLastName({ repLastName: data.repLastName }));
            dispatch(setUserFirstName({ userFirstName: data.userFirstName }));
            dispatch(setUserLastName({ userLastName: data.userLastName }));
            dispatch(setRepEmailRedux({ repEmail: data.repEmail }));
            dispatch(setRepTitle({ repTitle: data.repTitle }));
            dispatch(
              setMerchantTitleRedux({ merchantTitle: data.merchantTitle })
            );
            dispatch(
              setShareholderDirector({
                shareholdersDirectors: data.shareHoldersDirectors || [],
              })
            );
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId, dispatch]);

  const {
    repFirstName: repFirstNameRedux,
    repLastName: repLastNameRedux,
    userFirstName: userFirstNameRedux,
    userLastName: userLastNameRedux,
    repTitle: repTitleRedux,
    merchantTitle: merchantTitleRedux,
    shareholdersDirectors: shareholdersDirectorsRedux,
    repEmail: repEmailRedux,
  } = useSelector((state: RootState) => state.Borrep);

  const wasAuthCheckboxChecked =
    userFirstNameRedux !== null &&
    userLastNameRedux !== null &&
    repEmailRedux !== null &&
    repFirstNameRedux === userFirstNameRedux &&
    repLastNameRedux === userLastNameRedux &&
    userEmail === repEmailRedux;

  const directorEmailsRedux = shareholdersDirectorsRedux.map(
    (user) => user?.email
  );
  const wasDirectorChecboxChecked = directorEmailsRedux.includes(
    repEmailRedux ?? ""
  );

  const Navigate = useNavigate();

  const [authChecked, setAuthChecked] = useState(wasAuthCheckboxChecked);
  const [directorChecked, setDirectorChecked] = useState(
    wasDirectorChecboxChecked
  );

  const [representativeFirstName, setRepresentativeFirstName] = useState(
    repFirstNameRedux || ""
  );
  const [representativeLastName, setRepresentativeLastName] = useState(
    repLastNameRedux || ""
  );
  const [representative_title, setRepresentativeTitle] = useState(
    repTitleRedux || ""
  );
  const [merchantTitle, setMerchantTitle] = useState(merchantTitleRedux || "");
  const [merchantFirstName, setMerchantFirstName] = useState(
    userFirstNameRedux || ""
  );
  const [repEmail, setRepEmail] = useState(repEmailRedux || "");
  const [merchantLastName, setMerchantLastName] = useState(
    userLastNameRedux || ""
  );
  const [numOfShareholdersDirectors, setNumOfShareholdersDirectors] = useState(
    shareholdersDirectorsRedux?.length || 1
  );
  const [shareholdersDirectors, setShareholdersDirectors] = useState<
    ShareholderDirector[]
  >(shareholdersDirectorsRedux || []);

  const updateShareholderDirectorValue = (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => {
    if (shareholdersDirectors[index]?.email === repEmail && directorChecked) {
      if (field === ShareholderDirectorField.EMAIL) {
        setRepEmail(value);
      }
      if (field === ShareholderDirectorField.FIRSTNAME) {
        setRepresentativeFirstName(value);
      }
      if (field === ShareholderDirectorField.LASTNAME) {
        setRepresentativeLastName(value);
      }
    }

    setShareholdersDirectors((prev) => {
      const updatedShareholdersDirectors: ShareholderDirector[] = [...prev];
      updatedShareholdersDirectors[index] = {
        ...updatedShareholdersDirectors[index],
        [field]: value,
        [ShareholderDirectorField.ROLE]: CompanyRole.DIRECTOR,
      };
      return updatedShareholdersDirectors;
    });
  };

  const [error, setError] = useState({
    representative_first_name: "",
    representative_last_name: "",
    repEmail: "",
    userFirstName: "",
    userLastName: "",
    representative_title: "",
    merchantTitle: "",
    shareholderDirector: "",
  });

  const handleSubmit = () => {
    saveDraft(false);
    const customErr = { ...error };
    let valid = true;

    if (!representativeFirstName) {
      valid = false;
      customErr.representative_first_name =
        "Representative's First Name is required";
    }
    if (!representativeLastName) {
      valid = false;
      customErr.representative_last_name =
        "Representative's Last Name is required";
    }
    if (!repEmail) {
      valid = false;
      customErr.repEmail = "Representative's Email is required";
    }
    if (!merchantFirstName) {
      valid = false;
      customErr.userFirstName = "Your First Name is required";
    }
    if (!merchantLastName) {
      valid = false;
      customErr.userLastName = "Your Last Name is required";
    }
    if (!representative_title) {
      valid = false;
      customErr.representative_title = "Representative's Role is required";
    }
    if (!merchantTitle) {
      valid = false;
      customErr.merchantTitle = "Your Role is required";
    }

    if (!shareholdersDirectors || shareholdersDirectors?.length < 1) {
      valid = false;
      customErr.shareholderDirector = "Directors are required";
    } else {
      customErr.shareholderDirector = "";
    }

    if (shareholdersDirectors) {
      const emails: string[] = [];

      shareholdersDirectors.forEach((user, index) => {
        if (emails.includes(user.email)) {
          valid = false;
          toast.warn("Different Directors cannot have the same email");
        }

        emails.push(user.email);

        if (!user.role) {
          valid = false;
          toast.warn(`Director ${index + 1} Role is required`);
        }

        if (!user.firstName) {
          valid = false;
          toast.warn(`Director ${index + 1} First Name is required`);
        }

        if (!user.lastName) {
          valid = false;
          toast.warn(`Director ${index + 1} Last Name is required`);
        }

        if (!user.email) {
          valid = false;
          toast.warn(`Director ${index + 1} Email required`);
        } else if (!EMAIL_REGEX_VALIDATION.test(user.email)) {
          valid = false;
          toast.warn(`Director ${index + 1}'s email format is incorrect`);
        }

        if (userEmail === user.email) {
          valid = false;
          toast.warn(`Director ${index + 1} cannot have the same email as you`);
        }

        if (!directorChecked && user.email === repEmail) {
          valid = false;
          toast.warn(
            `Representative's email cannot be the same as Director ${
              index + 1
            } Email unless "Is the authorized representative one of the above directors?" checkbox is checked`
          );
        }
      });

      if (numOfShareholdersDirectors < 1) {
        valid = false;
        toast.warn(`Requires at least 1 Director`);
      }

      if (numOfShareholdersDirectors > 2) {
        valid = false;
        toast.warn(`Maximun Directors cannot exceed 2`);
      }
    }

    if (userEmail === repEmail && !authChecked) {
      valid = false;
      toast.warn(
        `Representative's email cannot be the same as your email unless "Will you be acting as an Authorized Representative?" checkbox is checked`
      );
    }

    if (valid) {
      dispatch(
        setRepFirstName({ repFirstName: representativeFirstName.trim() })
      );
      dispatch(setRepLastName({ repLastName: representativeLastName.trim() }));
      dispatch(setRepEmailRedux({ repEmail: repEmail }));
      dispatch(setUserFirstName({ userFirstName: merchantFirstName.trim() }));
      dispatch(setUserLastName({ userLastName: merchantLastName.trim() }));
      dispatch(setRepTitle({ repTitle: representative_title }));
      dispatch(setShareholderDirector({ shareholdersDirectors }));
      dispatch(setMerchantTitleRedux({ merchantTitle }));

      Navigate("/bor-bank");
    } else {
      setError(customErr);
    }
  };

  const handleBack = () => {
    saveDraft(false);
    dispatch(setRepFirstName({ repFirstName: representativeFirstName }));
    dispatch(setRepLastName({ repLastName: representativeLastName }));
    dispatch(setRepEmailRedux({ repEmail: repEmail }));
    dispatch(setUserFirstName({ userFirstName: merchantFirstName }));
    dispatch(setUserLastName({ userLastName: merchantLastName }));
    dispatch(setRepTitle({ repTitle: representative_title }));
    dispatch(setShareholderDirector({ shareholdersDirectors }));
    dispatch(setMerchantTitleRedux({ merchantTitle }));

    Navigate("/bor-biz-profile");
  };

  const directorEmails = shareholdersDirectors
    .map((user) => user?.email)
    .filter((email) => email && email.trim() !== "");
  const allDirectorsEmailPresent = directorEmails.length > 0;

  useEffect(() => {
    if (authChecked) {
      setRepresentativeLastName(merchantLastName);
      setRepresentativeFirstName(merchantFirstName);
      setRepresentativeTitle(merchantTitle);
      setRepEmail(userEmail as string);
      error.representative_last_name = "";
      error.representative_first_name = "";
      error.representative_title = "";
      error.repEmail = "";
    } else if (directorChecked) {
      if (!directorEmails.includes(repEmail)) {
        setRepresentativeLastName("");
        setRepresentativeFirstName("");
        setRepresentativeTitle("");
        setRepEmail("");
      }

      if (directorEmails.includes(repEmail)) {
        const directorInfo = shareholdersDirectors.filter(
          (user) => user.email === repEmail
        )[0];
        setRepresentativeLastName(directorInfo.lastName);
        setRepresentativeFirstName(directorInfo.firstName);
        setRepresentativeTitle("Director");
        setRepEmail(directorInfo.email);
        error.representative_last_name = "";
        error.representative_first_name = "";
        error.representative_title = "";
        error.repEmail = "";
      }

      if (!allDirectorsEmailPresent) {
        setDirectorChecked(false);
      }
    }
  }, [
    shareholdersDirectors,
    directorEmails,
    repEmail,
    repTitleRedux,
    authChecked,
    merchantLastName,
    merchantFirstName,
    repLastNameRedux,
    repFirstNameRedux,
    userEmail,
    merchantTitle,
    repEmailRedux,
    directorChecked,
    allDirectorsEmailPresent,
    error,
  ]);

  const onNumberOfShareholdersDirectorsChange = (value: number) => {
    error.shareholderDirector = "";
    setNumOfShareholdersDirectors(Number(value));

    if (value < numOfShareholdersDirectors && shareholdersDirectors[value]) {
      const updatedShareholdersDirectors = [...shareholdersDirectors];
      updatedShareholdersDirectors.pop();

      setShareholdersDirectors(updatedShareholdersDirectors);
    }
  };

  const handleAuthDirectorEmailChange = (email: string) => {
    const directorInfo = shareholdersDirectors.filter(
      (user) => user.email === email
    )[0];

    setRepresentativeLastName(directorInfo.lastName);
    setRepresentativeFirstName(directorInfo.firstName);
    setRepresentativeTitle("Director");
    setRepEmail(email);
  };

  const saveDraft = (showToast: Boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        repFirstName: representativeFirstName,
        repLastName: representativeLastName,
        repTitle: representative_title,
        userFirstName: merchantFirstName,
        userLastName: merchantLastName,
        repEmail: repEmail,
        merchantTitle: merchantTitle,
        authChecked: authChecked,
        directorChecked: directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div className={classes.container}>
      <AuthRepInfo />
      <div className={classes.inputBox}>
        <User
          authChecked={authChecked}
          setAuthChecked={setAuthChecked}
          error={error}
          setMerchantFirstName={setMerchantFirstName}
          merchantFirstName={merchantFirstName}
          setMerchantLastName={setMerchantLastName}
          merchantLastName={merchantLastName}
          setMerchantTitle={setMerchantTitle}
          merchantTitle={merchantTitle}
        />
        <img src={Line} alt="line" />
        <ShareholdersDirectors
          numOfShareholdersDirectors={numOfShareholdersDirectors}
          onNumberOfShareholdersDirectorsChange={
            onNumberOfShareholdersDirectorsChange
          }
          error={error.shareholderDirector}
          shareholdersDirectors={shareholdersDirectors}
          updateShareholderDirectorValue={updateShareholderDirectorValue}
        />
        <img src={Line} alt="line" />

        {!authChecked && (
          <Auth
            numOfDirectors={numOfShareholdersDirectors}
            allDirectorsEmailPresent={allDirectorsEmailPresent}
            directorChecked={directorChecked}
            setDirectorChecked={setDirectorChecked}
            error={error}
            repEmail={repEmail}
            directorEmails={directorEmails}
            handleAuthDirectorEmailChange={handleAuthDirectorEmailChange}
            setRepEmail={setRepEmail}
            setRepresentativeFirstName={setRepresentativeFirstName}
            representativeFirstName={representativeFirstName}
            setRepresentativeLastName={setRepresentativeLastName}
            representativeLastName={representativeLastName}
            setRepresentativeTitle={setRepresentativeTitle}
            representative_title={representative_title}
          />
        )}
        <div className={classes.buttonDivv}>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleBack}
              backgroundColor="#ffff"
              textColor="#338F86"
              children="Back"
              border="1px solid #338F86"
              width="100%"
            />
          </div>
          <ToastContainer />
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleSubmit}
              textColor="#ffff"
              backgroundColor="#338F86"
              children="Next"
              width="100%"
            />
          </div>
        </div>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={saveDraft}
            textColor={color.WHITE}
            backgroundColor={color.LOANTEXT}
            children="Save Draft"
            width="100%"
          />
        </div>
      </div>
    </div>
  );
};

export default BorrowerRep;
